import { useMutation } from "@tanstack/react-query";
import React, { useState, useContext } from "react";
import ApproveModal from "../../../components/elements/ApproveModal";
import CorrectionModal from "../../../components/elements/CorrectionModal";
import Modal from "../../../components/elements/Modal";
import RejectModal from "../../../components/elements/RejectModal";
import SnackbarAlert from "../../../components/elements/SnackbarAlert";
import SuccessModal from "../../../components/elements/SuccessModal";
import { useFormData } from "../../../helper/FormDataProvider";
import { StorageContext } from "../../../services/StorageProvider";
import {
  ClearanceStatus as Status,
  TRIP_PHASE,
  CORRECTION_OPTIONS,
} from "../../../utils/constants";
import { getCurrentTimestamp } from "../../../utils/dateUtils";
import "../css/EscortActionButtons.css";
import useLandingAuthApi from "../../../services/api/useLandingAuthApi";
import { airportCodeOnApprove, hasAirportAccess } from "../../../utils/Utils";
import Tooltip from "@mui/material/Tooltip";
import infoIcon from "../../../assets/images/info_icon.svg";

const EscortDepartureActionButtons = ({
  tripId,
  trackingId,
  officers,
  airportCodeOnApproved,
  clearanceStatus: initialClearanceStatus,
  correctionReason: initialCorrectionReason,
}) => {
  const { approveEscort, rejectEscort, correctionNeededEscort } =
    useLandingAuthApi();
  const { userData } = useFormData();
  const { airportData } = useContext(StorageContext);

  const departureData = {
    escortOfficers: officers,
    tripPhase: TRIP_PHASE.DEPARTURE,
  };

  const [clearanceStatus, setClearanceStatus] = useState(
    initialClearanceStatus
  );
  const [modalState, setModalState] = useState({
    isApproveModalOpen: false,
    isRejectModalOpen: false,
    isCorrectionNeedModalOpen: false,
  });
  const [selectedReason, setSelectedReason] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [commonError, setCommonError] = useState(null);

  // Check if user has access to the departure airport
  const hasDepartureAirportAccess = hasAirportAccess(
    userData,
    airportData,
    airportCodeOnApproved?.id
  );

  const isApproved =
    clearanceStatus === Status.CLEARANCE_GRANTED ||
    clearanceStatus === Status.PAID;
  const isRejected = clearanceStatus === Status.CLEARANCE_REJECTED;

  const isCancelled = clearanceStatus === Status.CANCELLED;
  const isCorrectionRequested = clearanceStatus === Status.CORRECTION_REQUESTED;

  const allDisabled =
    isApproved || isRejected || isCancelled || isCorrectionRequested || !hasDepartureAirportAccess;

  const [successModalData, setSuccessModalData] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  const closeSuccessModal = () => {
    setSuccessModalData({
      isOpen: false,
      title: "",
      message: "",
    });
  };

  const { mutate: approveEscortMutate, isPending: isApprovePending } =
    useMutation({
      mutationFn: ({ tripId, data }) => approveEscort(tripId, data),
      onSuccess: () => {
        setClearanceStatus(Status.CLEARANCE_GRANTED || Status.PAID);
        setSuccessModalData({
          isOpen: true,
          title: "Approved",
          message:
            "You have successfully approved the Special Onboarding Request",
        });
        closeModal("isApproveModalOpen");
      },
      onError: (error) => {
        setCommonError(error.message);

        closeModal("isApproveModalOpen");
      },
    });

  const { mutate: rejectEscortMutate, isPending: isRejectPending } =
    useMutation({
      mutationFn: ({ tripId, data }) => rejectEscort(tripId, data),
      onSuccess: () => {
        setClearanceStatus(Status.CLEARANCE_REJECTED);
        setSuccessModalData({
          isOpen: true,
          title: "Rejected",
          message: "The request was rejected for this Trip",
        });

        closeModal("isRejectModalOpen");
      },
      onError: (error) => {
        setCommonError(error.message);
        closeModal("isRejectModalOpen");
      },
    });

  const {
    mutate: correctionNeededEscortMutate,
    isPending: isCorrectionPending,
  } = useMutation({
    mutationFn: ({ tripId, data }) => correctionNeededEscort(tripId, data),
    onSuccess: () => {
      setClearanceStatus(Status.CORRECTION_REQUESTED);
      setSuccessModalData({
        isOpen: true,
        title: "Correction Needed",
        message: "Correction is requested for this Trip",
      });
      closeModal("isCorrectionNeedModalOpen");
    },
    onError: (error) => {
      setCommonError(error.message);
      closeModal("isCorrectionNeedModalOpen");
    },
  });

  const openModal = (modalName) => {
    if (modalName === "isApproveModalOpen") {
      if (officers.length < 2) {
        setCommonError("At least 2 officers must be added before approving");
        setTimeout(() => setCommonError(null), 3000);
        return;
      }

      // Check for duplicate officers
      const uniqueOfficerIds = new Set(
        officers.map((officer) => officer.officerId)
      );
      if (uniqueOfficerIds.size !== officers.length) {
        setCommonError("Cannot have duplicate officers");
        setTimeout(() => setCommonError(null), 3000);
        return;
      }
    }

    setModalState({
      isApproveModalOpen: false,
      isRejectModalOpen: false,
      isCorrectionNeedModalOpen: false,
      [modalName]: true,
    });
    setCommonError(null);
  };

  const closeModal = (modalName) => {
    setModalState((prevState) => ({
      ...prevState,
      [modalName]: false,
    }));
  };

  const handleCorrectNeeded = (event) => {
    event.preventDefault();
    correctionNeededEscortMutate({
      tripId,
      data: { correctionReason, tripPhase: TRIP_PHASE.DEPARTURE },
    });
  };

  const handleApproveEscort = () => {
    if (officers.length < 2) {
      setCommonError("At least 2 officers must be added before approving");
      return;
    }

    // Check for duplicate officers
    const uniqueOfficerIds = new Set(
      officers.map((officer) => officer.officerId)
    );
    if (uniqueOfficerIds.size !== officers.length) {
      setCommonError("Cannot have duplicate officers");
      return;
    }

    approveEscortMutate({
      tripId,
      data: departureData,
    });
  };

  const handleRejectEscort = (e) => {
    e.preventDefault();
    rejectEscortMutate({
      tripId,
      data: {
        rejectionReason: selectedReason,
        tripPhase: TRIP_PHASE.DEPARTURE,
      },
    });
  };

  const getCorrectionReasonLabel = (value) => {
    const option = CORRECTION_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const getButtonProps = (type) => {
    if (type === "approve") {
      const insufficientOfficers = officers.length < 2;
      return {
        text: isApproved ? "Approved" : "Approve",
        className: `escort-trip-details-btn ${isApproved
          ? "approved not-clickable"
          : "escort-trip-details-approve-btn"
          }`,
        onClick: isApproved ? null : () => openModal("isApproveModalOpen"),
        disabled: allDisabled && !isApproved,
      };
    } else if (type === "reject") {
      return {
        text: isRejected ? "Rejected" : "Reject",
        className: `escort-trip-details-btn ${isRejected
          ? "rejected not-clickable"
          : "escort-trip-details-reject-btn"
          }`,
        onClick: isRejected ? null : () => openModal("isRejectModalOpen"),
        disabled: allDisabled && !isRejected,
      };
    } else if (type === "correction") {
      return {
        text: (
          <div className="escort-trip-details-correction-button-content">
            <span>
              {isCorrectionRequested ? "Correction Requested" : "Correction Needed"}
            </span>
            {isCorrectionRequested && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setTooltipOpen(!tooltipOpen);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  setTooltipOpen(!tooltipOpen);
                }}
              >
                <Tooltip
                  title={getCorrectionReasonLabel(initialCorrectionReason)}
                  arrow
                  open={tooltipOpen}
                  onClose={() => setTooltipOpen(false)}
                >
                  <img
                    src={infoIcon}
                    alt="info"
                    className="escort-trip-details-correction-info-icon"
                  />
                </Tooltip>
              </div>
            )}
          </div>
        ),
        className: `escort-trip-details-btn escort-trip-details-correction-btn ${isCorrectionRequested ? "not-clickable" : ""
          }`,
        onClick: isCorrectionRequested
          ? (e) => e.preventDefault()
          : () => openModal("isCorrectionNeedModalOpen"),
        disabled: allDisabled && !isCorrectionRequested,
      };
    }
    return {};
  };

  return (
    <div>
      {commonError && (
        <SnackbarAlert type={"error"}>{commonError}</SnackbarAlert>
      )}

      {!hasDepartureAirportAccess && (
        <div className="action-buttons-message">
          <span className="info-icon">ℹ️</span>
          You don't have access to the departure airport, so you are not authorized to Approve, Reject, or request Corrections for this Special Onboarding request.
        </div>
      )}

      <div className="escort-trip-details-action-buttons">
        <button {...getButtonProps("approve")}>
          {getButtonProps("approve").text}
        </button>
        <button {...getButtonProps("reject")}>
          {getButtonProps("reject").text}
        </button>
        <button {...getButtonProps("correction")}>
          {getButtonProps("correction").text}
        </button>
      </div>

      <Modal
        isOpen={modalState.isRejectModalOpen}
        onClose={() => closeModal("isRejectModalOpen")}
      >
        <RejectModal
          selectedReason={selectedReason}
          onReasonChange={(e) => setSelectedReason(e.target.value)}
          isLoading={isRejectPending}
          onRejectClick={handleRejectEscort}
        />
      </Modal>

      <Modal
        isOpen={modalState.isCorrectionNeedModalOpen}
        onClose={() => closeModal("isCorrectionNeedModalOpen")}
      >
        <CorrectionModal
          onCorrectNeeded={handleCorrectNeeded}
          reasonForCorrection={(e) => setCorrectionReason(e.target.value)}
          isLoading={isCorrectionPending}
          correctionOptions={CORRECTION_OPTIONS}

        />
      </Modal>

      <Modal
        isOpen={modalState.isApproveModalOpen}
        onClose={() => closeModal("isApproveModalOpen")}
      >
        <ApproveModal
          email={userData?.email}
          tripId={trackingId}
          airport={airportCodeOnApprove(
            airportCodeOnApproved?.iataCode,
            airportCodeOnApproved?.icaoCode
          )}
          timestamp={getCurrentTimestamp()}
          onApprove={handleApproveEscort}
          isLoading={isApprovePending}
          message={"Do you want to approve the Special Onboarding?"}
        />
      </Modal>
      <SuccessModal
        isOpen={successModalData.isOpen}
        onClose={closeSuccessModal}
        title={successModalData.title}
        message={successModalData.message}
      />
    </div>
  );
};

export default EscortDepartureActionButtons;
